import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PlusButton } from '../../ui/PlusButton';
import { caseCartouche, caseConstraints, caseMedia, caseTechnical } from '../../../data/caseStudy';
export function CaseTeaser() {
  const imageRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: imageRef,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  return <section aria-labelledby="case-title" className="bg-canvas py-24 md:py-32" data-id="element-16" data-mp="3sp3lztsc2z1b">
      <div className="shell" data-id="element-17" data-mp="1hrb1wi1nhtu">
        <div className="grid gap-6 lg:grid-cols-12 lg:items-end" data-id="element-18" data-mp="1gppuz7bdeye">
          <h2 id="case-title" className="text-[clamp(32px,4.4vw,64px)] font-medium leading-[1] tracking-[-0.04em] lg:col-span-7" data-id="element-19" data-mp="5jup8sw3l0it">
            Chaque projet, documenté comme une pièce d’ingénierie.
          </h2>
          <div className="lg:col-span-5" data-id="element-20" data-mp="1h0uhjoig3fcu">
            <p className="text-lg leading-relaxed text-mute" data-id="element-21" data-mp="l8n823ulo1up">
              Cartouche, données techniques, contraintes logistiques résolues et médiathèque de fabrication. Aperçu
              d’une fiche — le portfolio complet arrive bientôt.
            </p>
            <div className="mt-6" data-id="element-22" data-mp="bc229garampt">
              <PlusButton to="/projets" variant="ink" data-id="element-23" data-mp="795gxhtu0ci6">
                Portfolio · bientôt
              </PlusButton>
            </div>
          </div>
        </div>

        <div ref={imageRef} className="relative mt-14 h-[560px] overflow-hidden bg-night md:h-[640px]" data-id="element-24" data-mp="2bcacnom9ghd">
          <motion.img src="https://cdn.magicpatterns.com/patterns/generated-images/8b55b88a-a916-4bc6-869b-3c866dfa0f47.jpg" alt="Hall de fabrication avec décors et sculptures en cours de construction" style={{
          y,
          scale: 1.18
        }} className="absolute inset-0 h-full w-full object-cover" data-id="element-25" data-mp="3tmdr5dlg8hx0" />
          <div className="absolute inset-0 bg-night/25" data-id="element-26" data-mp="3aqdv9rpcou85" />

          <article className="absolute bottom-0 left-0 w-full bg-paper p-6 md:bottom-8 md:left-8 md:w-[460px] md:p-8" data-id="element-27" data-mp="311qg0e49gmet">
            <p className="font-mono text-[11px] uppercase tracking-wider text-ember" data-id="element-28" data-mp="353ttt9eek339">Étude de cas · aperçu</p>
            <h3 className="mt-2 text-2xl font-medium tracking-[-0.03em] md:text-3xl" data-id="element-29" data-mp="2r4cc1dzixq3e">Nébuleuse — Arena Tour</h3>
            <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 border-t border-line pt-5" data-id="element-30" data-mp="2w3f47xueo1ty">
              {caseCartouche.map(item => <div key={item.label} data-id="element-31" data-mp="3jukk2d0ciu49">
                  <dt className="text-xs text-mute" data-id="element-32" data-mp="3orkpclpdfsub">{item.label}</dt>
                  <dd className="mt-0.5 text-[15px] font-medium" data-id="element-33" data-mp="3a2eu18tnmmrp">{item.value}</dd>
                </div>)}
            </dl>
          </article>

          <dl className="absolute right-8 top-8 hidden w-[320px] space-y-4 bg-night/75 p-5 text-white backdrop-blur-sm lg:block" data-id="element-34" data-mp="3f06tcnwmpb5x">
            {caseTechnical.map(item => <div key={item.label} data-id="element-35" data-mp="bcocdat4ljgw">
                <dt className="font-mono text-[11px] uppercase tracking-wider text-ember" data-id="element-36" data-mp="bdpshtp00w5h">{item.label}</dt>
                <dd className="mt-1 text-sm leading-relaxed text-white/85" data-id="element-37" data-mp="1w0pg8pwyfv3m">{item.value}</dd>
              </div>)}
          </dl>
        </div>

        <div className="mt-px grid grid-cols-2 bg-paper lg:grid-cols-4" data-id="element-38" data-mp="24vdlig2j5csf">
          {caseConstraints.map(c => <div key={c.label} className="border-b border-r border-line p-5 md:p-7 lg:border-b-0 [&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r lg:last:border-r-0" data-id="element-39" data-mp="20utmf6yebz3n">
              <p className="text-sm text-mute" data-id="element-40" data-mp="185mr0gu8npmm">{c.label}</p>
              <p className="mt-1 text-2xl font-medium tracking-[-0.03em] md:text-3xl" data-id="element-41" data-mp="1877n4aw7bgml">{c.value}</p>
            </div>)}
        </div>

        <div className="mt-10" data-id="element-42" data-mp="1h59quy7bkndk">
          <h3 className="text-sm text-mute" data-id="element-43" data-mp="1h484vyormgh2">Médiathèque du projet</h3>
          <ul className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4" data-id="element-44" data-mp="2xirmcp9cmsht">
            {caseMedia.map(m => <li key={m.label} data-id="element-45" data-mp="2sjwruq20rfhv">
                <Link to="/projets" className="group block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ember" data-id="element-46" data-mp="2smsqkgloy87d">
                  <div className="aspect-[4/3] overflow-hidden bg-night" data-id="element-47" data-mp="3sur4hqr6eaj9">
                    <img src={m.src} alt={m.alt} className="h-full w-full object-cover transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:scale-105" data-id="element-48" data-mp="1nevw04cz5ey" />
                  </div>
                  <p className="mt-2 flex items-center gap-2 text-sm font-medium" data-id="element-49" data-mp="fmxqrr85sk9b">
                    <span className="h-1.5 w-1.5 bg-ember" aria-hidden data-id="element-50" data-mp="ke5zel1o01lf" />
                    {m.label}
                  </p>
                </Link>
              </li>)}
          </ul>
        </div>
      </div>
    </section>;
}